import React, { useEffect, useState } from "react";
import { RightOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { message, Select } from "antd";
import styled from "styled-components";
import moment from "moment";
import usericon from "../../../../src/assets/Images/usericon.png";
import clock from "../../../../src/assets/Images/clock.png";
import placeholderImg from "../../../../src/assets/Images/course1.png";
import FetchData from "../../../hooks/FetchData";
import PostUpdateData from "../../../hooks/postUpdate";

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

const Customstyle = styled.div`
  padding: 20px;
  font-family: "DM Sans", sans-serif;

  .custom-field {
    color: grey;
    font-weight: 400;
    font-size: 16px;
    cursor: pointer;
  }
  .course-text {
    font-weight: 400;
    font-size: 16px;
  }
  .user {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .total {
    text-align: left;
  }
  .crumb-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
  }

  .hero {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    background: #eafaf3;
    border-radius: 12px;
    padding: 24px;
  }

  .hero-title {
    font-size: 22px;
    font-weight: 600;
    color: #1f243b;
    margin-bottom: 12px;
  }

  .enroll-row {
    display: flex;
    align-items: center;
    margin-top: 16px;
  }

  .enroll-button {
    margin-top: 0;
    background: #c2fae5;
    border: 1px solid #1f243b;
    border-radius: 8px;
    padding: 8px 24px;
    font-size: 14px;
    cursor: pointer;
  }

  .enroll-button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .hero-thumb {
    width: 240px;
    height: 140px;
    object-fit: cover;
    border-radius: 8px;
    flex-shrink: 0;
  }

  .description {
    margin-top: 24px;
    color: #636980;
    line-height: 1.6;
  }

  .units-section {
    margin-top: 24px;
  }

  .units-heading {
    font-weight: 600;
    color: #1f243b;
    border-bottom: 2px solid #1f243b;
    display: inline-block;
    padding-bottom: 6px;
    margin-bottom: 12px;
  }

  .unit-row {
    padding: 12px 0;
    border-bottom: 1px solid #e2e2e8;
    color: #1f243b;
  }

  .unit-video {
    margin-top: 10px;

    iframe,
    video {
      max-width: 100%;
      border-radius: 8px;
      background: #000;
    }
  }
`;

function Viewdetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useSelector((state) => state.authTracking.userInfo);
  const courseId = location.state?.course_id;

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [people, setPeople] = useState([]);
  const [selectedPeopleId, setSelectedPeopleId] = useState(null);

  useEffect(() => {
    const fetchCourse = async () => {
      if (!userInfo?.clientId || !courseId) return;
      setLoading(true);
      try {
        const response = await FetchData({
          endPoint: `/course/getcoursebyid/${userInfo.clientId}?course_id=${courseId}`,
        });
        setCourse(response?.response || null);
      } catch (error) {
        console.error("Error fetching course details:", error);
        message.error("Unable to load this course");
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [userInfo?.clientId, courseId]);

  useEffect(() => {
    // Enrollment is tracked per person (tymeplushrlmspeople.people_id), the
    // same identity Group -> People uses, since the logged-in admin session
    // has no matching people record of its own.
    const fetchPeople = async () => {
      if (!userInfo?.clientId) return;
      try {
        const response = await FetchData({
          endPoint: `/people/getuser/${userInfo.clientId}`,
        });
        if (response?.statusCode === 200 && Array.isArray(response?.response)) {
          setPeople(response.response);
        }
      } catch (error) {
        console.error("Error fetching people:", error);
      }
    };
    fetchPeople();
  }, [userInfo?.clientId]);

  const handleEnroll = async () => {
    if (!courseId || !userInfo?.clientId) return;
    if (!selectedPeopleId) {
      message.error("Select a person to enroll first");
      return;
    }
    setEnrolling(true);
    try {
      const response = await PostUpdateData({
        endPoint: `/course/enroll/${userInfo.clientId}`,
        method: "POST",
        obj: { course_id: courseId, people_id: selectedPeopleId },
      });
      if (response?.statusCode === 201) {
        message.success("Enrolled successfully");
      } else if (response?.statusCode === 200) {
        message.info("This person is already enrolled in this course");
      } else {
        message.error("Something went wrong while enrolling");
      }
    } catch (error) {
      console.error("Error enrolling in course:", error);
      message.error("Something went wrong while enrolling");
    } finally {
      setEnrolling(false);
    }
  };

  if (!courseId) {
    return (
      <Customstyle>
        <p>No course selected. Go back to the course list and pick a course.</p>
      </Customstyle>
    );
  }

  return (
    <Customstyle>
      <div className="total">
        <div className="crumb-row">
          <p
            className="custom-field"
            onClick={() => {
              navigate("/courselist");
            }}
          >
            Course List
          </p>
          <RightOutlined style={{ fontSize: 12 }} />
          <p className="course-text">Course Details</p>
        </div>

        {loading && <p>Loading course...</p>}

        {!loading && course && (
          <>
            <div className="hero">
              <div style={{ flex: 1 }}>
                <p className="hero-title">{course.course_name}</p>

                <div className="course-text user">
                  <img src={usericon} alt="author" />
                  <div>{course.conducted_by || "—"}</div>
                </div>

                <div className="cb">
                  <div className="course-text user">
                    <img src={clock} alt="updated" />
                    <div>
                      <span className="auth">Last Updated:</span>{" "}
                      {course.updated_at
                        ? moment(course.updated_at).format("Do MMM YYYY")
                        : "—"}
                    </div>
                  </div>
                </div>

                <div className="enroll-row">
                  <Select
                    className="enroll-person-select"
                    style={{ minWidth: 220, marginRight: 12 }}
                    placeholder="Select a person"
                    value={selectedPeopleId}
                    onChange={setSelectedPeopleId}
                    options={people.map((p) => ({
                      value: p.people_id,
                      label: `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.email || `#${p.people_id}`,
                    }))}
                  />
                  <button className="enroll-button" onClick={handleEnroll} disabled={enrolling}>
                    {enrolling ? "Enrolling..." : "Enroll Now"}
                  </button>
                </div>
              </div>

              <img
                src={course.course_profile || placeholderImg}
                alt={course.course_name}
                className="hero-thumb"
              />
            </div>

            <p className="description">{course.course_describtion}</p>

            {Array.isArray(course.units) && course.units.length > 0 && (
              <div className="units-section">
                <div className="units-heading">Content</div>
                {course.units.map((unit, index) => (
                  <div className="unit-row" key={index}>
                    {unit.title || unit.type}
                    {unit.type === "Video" && unit.mediaUrl && (
                      <div className="unit-video">
                        {getYouTubeEmbedUrl(unit.mediaUrl) ? (
                          <iframe
                            width="560"
                            height="315"
                            src={getYouTubeEmbedUrl(unit.mediaUrl)}
                            title={unit.title || "Video"}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <video width="560" height="315" controls src={unit.mediaUrl} />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {!loading && !course && <p>This course could not be found.</p>}
      </div>
    </Customstyle>
  );
}

export default Viewdetail;
